/* ============================================================
   ZNAKI · scroll.js
   Wirtualny, nieskończony scroll z bezwładnością.
   Wejścia: kółko / trackpad, przeciąganie (mysz i dotyk),
   klawiatura, autodryf po bezczynności.
   Jednostką jest vh (1.0 = wysokość viewportu).
   ============================================================ */

import { PARAMS } from './config';
import { clamp, clamp01 } from './utils';

/* elementy interfejsu strony, nad którymi wejścia NIE ruszają tablicy */
const UI_SELECTOR = 'header, footer, #box-header, #box-footer, #box-global-search, ' +
  '#wj-playground-panel, a, button, input, textarea, select';

/* dialogi (menu, wyszukiwarka) otwarte na <html> — klawiatura należy wtedy do nich */
const DIALOG_CLASSES = ['main-menu-open', 'search-open', 'venue-map-open', 'playground-info-open'];

class ScrollEngine {

  constructor() {
    this.target = 0;        /* dokąd zmierzamy [vh] */
    this.value = 0;         /* wygładzona pozycja [vh] */
    this.vel = 0;           /* prędkość [vh/s] */
    this.velSmooth = 0;     /* prędkość wygładzona (do efektów) */

    this.holdTarget = 0;    /* 1 gdy użytkownik "przytrzymuje" (skupienie) */

    this._drag = null;      /* stan przeciągania */
    this._samples = [];     /* próbki do wyliczenia rozpędu po puszczeniu */
    this._fling = 0;        /* rozpęd po przeciągnięciu [vh/s] */
    this._intro = null;     /* automatyczny przesuw po starcie instalacji */
    this._lastInput = performance.now();
    this._holdTimer = null;

    this.onFirstInput = null;  /* callback — np. schowanie podpowiedzi */
    this._hadInput = false;

    this._el = null;
    /* związane referencje — konieczne, by móc odpiąć nasłuchy przy zmianie widoku */
    this._onWheelBound = (e) => this._onWheel(e);
    this._onDownBound  = (e) => this._onDown(e);
    this._onMoveBound  = (e) => this._onMove(e);
    this._onUpBound    = (e) => this._onUp(e);
    this._onKeyBound   = (e) => this._onKey(e);
  }

  /* --------------------------------------------------------- */

  /* wejścia scrolla podpinamy do kontenera instalacji, a nie do okna —
     nagłówek i stopka strony leżą nad nim i zachowują własne zachowanie */
  attach(el) {
    this._el = el;
    el.addEventListener('wheel', this._onWheelBound, { passive: false });
    el.addEventListener('pointerdown', this._onDownBound);

    window.addEventListener('pointermove', this._onMoveBound);
    window.addEventListener('pointerup', this._onUpBound);
    window.addEventListener('pointercancel', this._onUpBound);
    window.addEventListener('keydown', this._onKeyBound);
  }

  detach() {
    if (this._el) {
      this._el.removeEventListener('wheel', this._onWheelBound, { passive: false });
      this._el.removeEventListener('pointerdown', this._onDownBound);
      this._el = null;
    }
    window.removeEventListener('pointermove', this._onMoveBound);
    window.removeEventListener('pointerup', this._onUpBound);
    window.removeEventListener('pointercancel', this._onUpBound);
    window.removeEventListener('keydown', this._onKeyBound);
    this._cancelHold();
    this._drag = null;
    this._samples.length = 0;
    this._intro = null;
  }

  /* wejścia nad interfejsem (nagłówek, stopka, panel) nie ruszają tablicy */
  _overUI(e) {
    return !!(e.target && e.target.closest && e.target.closest(UI_SELECTOR));
  }

  _dialogOpen() {
    const cl = document.documentElement.classList;
    return DIALOG_CLASSES.some((c) => cl.contains(c));
  }

  _markInput() {
    this._lastInput = performance.now();
    this._fling = 0;
    this._intro = null;     /* gest użytkownika przerywa wejście natychmiast */
    if (!this._hadInput) {
      this._hadInput = true;
      if (this.onFirstInput) this.onFirstInput();
    }
  }

  /* --- kółko / trackpad -------------------------------------- */

  _onWheel(e) {
    if (this._overUI(e)) return;
    e.preventDefault();
    let dy = e.deltaY;
    if (e.deltaMode === 1) dy *= 16;                        /* linie -> px */
    else if (e.deltaMode === 2) dy *= window.innerHeight;   /* strony -> px */
    const P = PARAMS.scroll;
    this.target += (dy / window.innerHeight) * P.wheelSpeed;
    this._markInput();
    this._cancelHold();
  }

  /* --- przeciąganie + przytrzymanie --------------------------- */

  _onDown(e) {
    if (e.button !== undefined && e.button !== 0) return;
    if (this._overUI(e)) return;
    this._drag = { y: e.clientY, x: e.clientX, moved: 0, t: performance.now() };
    this._samples.length = 0;
    this._pushSample(e.clientY);

    /* przytrzymanie w miejscu = skupienie (grawitacja osi) */
    this._holdTimer = setTimeout(() => {
      if (this._drag && this._drag.moved < 9) this.holdTarget = 1;
    }, 240);
  }

  _onMove(e) {
    if (!this._drag) return;
    const dy = e.clientY - this._drag.y;
    this._drag.moved += Math.abs(dy) + Math.abs(e.clientX - this._drag.x);
    this._drag.y = e.clientY;
    this._drag.x = e.clientX;

    if (this._drag.moved > 9) this._cancelHold();

    /* przesuwamy zawartość razem z palcem/kursorem */
    this.target -= (dy / window.innerHeight) * PARAMS.scroll.dragSpeed;
    this._pushSample(e.clientY);
    this._markInput();
  }

  _onUp() {
    if (!this._drag) return;

    /* rozpęd z ostatnich ~120 ms ruchu */
    const now = performance.now();
    const recent = this._samples.filter((s) => now - s.t < 120);
    if (recent.length >= 2) {
      const a = recent[0], b = recent[recent.length - 1];
      const dt = (b.t - a.t) / 1000;
      if (dt > 0.005) {
        const vyPx = (b.y - a.y) / dt;                       /* px/s */
        this._fling = -(vyPx / window.innerHeight) * PARAMS.scroll.dragSpeed;
        this._fling = clamp(this._fling, -6, 6);             /* vh/s */
      }
    }
    this._drag = null;
    this._cancelHold();
  }

  _pushSample(y) {
    this._samples.push({ y, t: performance.now() });
    if (this._samples.length > 24) this._samples.shift();
  }

  _cancelHold() {
    clearTimeout(this._holdTimer);
    this.holdTarget = 0;
  }

  /* --- klawiatura --------------------------------------------- */

  _onKey(e) {
    if (e.target && /^(INPUT|TEXTAREA|SELECT|BUTTON|A)$/.test(e.target.tagName)) return;
    if (this._dialogOpen()) return;
    let d = 0;
    switch (e.key) {
      case 'ArrowDown': d = 0.22; break;
      case 'ArrowUp':   d = -0.22; break;
      case 'PageDown':  d = 0.9; break;
      case 'PageUp':    d = -0.9; break;
      case ' ':         d = e.shiftKey ? -0.9 : 0.9; break;
      case 'Home':      this.target = 0; this._markInput(); return;
      default: return;
    }
    e.preventDefault();
    this.target += d;
    this._markInput();
  }

  /* --- wejście instalacji -------------------------------------- */

  /* Po kliknięciu "rozpocznij" tablica sama przesuwa się o `dist` [vh]
     w czasie `dur` [s] — tyle, ile trzeba, żeby figury wjechały w kadr.
     Wygląda to jak przewinięcie strony przez użytkownika i tak samo
     brzmi (tło reaguje na prędkość). Pierwszy gest użytkownika kasuje
     przesuw — od tej chwili tablicę prowadzi wyłącznie on. */
  startIntro(dist, dur) {
    /* wejścia sprzed startu (kółko nad ekranem wprowadzającym) nie przenoszą
       się na tablicę — wejście rusza z pozycji, którą widać */
    this.target = this.value;
    this._fling = 0;
    if (!(dur > 0)) return;
    this._intro = { from: this.value, dist: dist || 0, dur, t: 0 };
  }

  get introRunning() {
    return !!this._intro;
  }

  /* --- krok symulacji ----------------------------------------- */

  update(dt, reducedMotion) {
    const P = PARAMS.scroll;
    const now = performance.now();

    /* automatyczny przesuw wejścia — wyprzedza wszystkie inne wpływy */
    if (this._intro) {
      const I = this._intro;
      I.t = Math.min(I.dur, I.t + dt);
      const e = 1 - Math.pow(1 - I.t / I.dur, 3);   /* ease-out cubic */
      this.target = I.from + I.dist * e;
      this._lastInput = now;                        /* dryf liczy się po wejściu */
      if (I.t >= I.dur) this._intro = null;
    }

    /* rozpęd po przeciągnięciu — wygasa wykładniczo */
    if (Math.abs(this._fling) > 0.001) {
      this.target += this._fling * dt;
      this._fling *= Math.exp(-dt * 2.6);
    } else {
      this._fling = 0;
    }

    /* autodryf: instalacja czyta się sama, gdy nikt jej nie dotyka */
    if (!reducedMotion && P.autoDrift > 0 && !this._drag && this.holdTarget === 0) {
      const idleFor = (now - this._lastInput) / 1000 - P.idleDelay;
      if (idleFor > 0) {
        const ramp = clamp01(idleFor / 4);                   /* łagodny rozbieg 4 s */
        this.target += P.autoDrift * ramp * ramp * dt;
      }
    }

    /* pościg za celem (bezwładność) — stała niezależna od FPS */
    const k = 1 - Math.exp(-dt * 60 * P.ease);
    const prev = this.value;
    this.value += (this.target - this.value) * k;

    this.vel = (this.value - prev) / Math.max(dt, 1e-4);
    this.velSmooth += (this.vel - this.velSmooth) * Math.min(1, dt * 7);
  }
}

export default ScrollEngine;
